import { getBoardToolLabel, summarizeToolResult } from "./agent-tool-meta";
import type { AgentChatAssistantPart } from "./types";

export function assistantPartsHaveVisibleText(
	parts: AgentChatAssistantPart[],
): boolean {
	return parts.some(
		(part) => part.type === "text" && part.text.trim().length > 0,
	);
}

export function assistantPartsAwaitUserChoice(
	parts: AgentChatAssistantPart[],
): boolean {
	return parts.some((part) => part.type === "choice-request");
}

function buildFallbackTextFromToolResult(
	toolName: string,
	output: unknown,
): string {
	if (toolName === "createWhiteboard") {
		const record =
			output && typeof output === "object"
				? (output as Record<string, unknown>)
				: null;
		const name =
			typeof record?.name === "string" ? record.name.trim() : undefined;
		if (name) {
			return `Created a new whiteboard called "${name}".`;
		}
		return "Created a new whiteboard.";
	}

	if (toolName === "createFolder") {
		const record =
			output && typeof output === "object"
				? (output as Record<string, unknown>)
				: null;
		const name =
			typeof record?.name === "string" ? record.name.trim() : undefined;
		if (name) {
			return `Created folder "${name}".`;
		}
		return "Created a new folder.";
	}

	const label = getBoardToolLabel(toolName);
	const summary = summarizeToolResult(toolName, output);
	if (summary) {
		return `${label}: ${summary}.`;
	}

	return `Finished ${label.toLowerCase()}.`;
}

/**
 * Haiku (and similar) sometimes stop after tools with no text part.
 * Synthesize a short user-visible reply so the chat never ends blank.
 */
export function ensureAssistantVisibleReply(
	parts: AgentChatAssistantPart[],
): AgentChatAssistantPart[] {
	if (assistantPartsHaveVisibleText(parts)) {
		return parts;
	}

	if (assistantPartsAwaitUserChoice(parts)) {
		return parts;
	}

	const toolResults = parts.filter((part) => part.type === "tool-result");
	if (toolResults.length === 0) {
		return parts;
	}

	const last = toolResults[toolResults.length - 1];
	if (last.type !== "tool-result") {
		return parts;
	}

	return [
		...parts,
		{
			type: "text",
			text: buildFallbackTextFromToolResult(last.toolName, last.output),
		},
	];
}
